import type { AuthMembership, AuthQuotaMetric } from '@/features/auth/types'
import type { AssistantToolCatalogAnyDefinition } from '@/features/assistant/tool-catalog'
import type { AssistantToolContext } from '@/features/assistant/tool-context'
import { ASSISTANT_TOOL_ERROR_CODES, AssistantToolError } from '@/features/assistant/tool-errors'

function collectAccessTags(summary: AuthMembership | null): Set<string> {
  return new Set([
    ...(summary?.appFeatureFlags ?? []),
    ...(summary?.aiEntitlements ?? []),
    ...(summary?.automationEntitlements ?? []),
  ])
}

function getQuotaRemaining(summary: AuthMembership | null, metric: AuthQuotaMetric): number | null {
  const quotas = summary?.quotas
  if (!quotas) {
    return null
  }

  // 单一时长桶：优先从 raw 读，fallback 到命名属性
  if (typeof quotas.raw[metric] === 'number') {
    return quotas.raw[metric] ?? null
  }

  if (metric === 'ai_processing_seconds') {
    return quotas.aiProcessingSecondsRemaining
  }

  return null
}

function hasPaidAccess(summary: AuthMembership | null): boolean {
  const planCode = summary?.planCode?.trim().toLowerCase()
  const status = summary?.status?.trim().toLowerCase()

  if (!planCode || planCode === 'free' || planCode === 'fastcut_free') {
    return false
  }

  return status === 'active' || status === 'trialing' || status === 'grace_period'
}

function assertIncluded(
  toolName: string,
  summary: AuthMembership | null,
  availableTags: Set<string>,
  requiredValues: string[] | undefined,
  type: 'appFeatureFlags' | 'aiEntitlements' | 'automationEntitlements',
) {
  if (!requiredValues?.length) {
    return
  }

  const missing = requiredValues.filter((value) => !availableTags.has(value))
  if (missing.length === 0) {
    return
  }

  throw new AssistantToolError(
    hasPaidAccess(summary)
      ? ASSISTANT_TOOL_ERROR_CODES.featureNotIncludedInPlan
      : ASSISTANT_TOOL_ERROR_CODES.subscriptionRequired,
    `Current JIAN access does not include ${toolName}.`,
    {
      details: {
        toolName,
        missing,
        type,
      },
    },
  )
}

export function assertAssistantToolAccess(
  definition: AssistantToolCatalogAnyDefinition,
  context: AssistantToolContext,
): void {
  const policy = definition.commercialPolicy
  if (!policy) {
    return
  }

  const summary = context.accessSummary
  if (policy.requiresAuth && !context.isUserSignedIn) {
    throw new AssistantToolError(
      ASSISTANT_TOOL_ERROR_CODES.authRequired,
      `Authentication is required before running ${definition.name}.`,
      {
        details: {
          toolName: definition.name,
        },
      },
    )
  }

  const availableTags = collectAccessTags(summary)
  assertIncluded(
    definition.name,
    summary,
    availableTags,
    policy.requiredAppFeatures,
    'appFeatureFlags',
  )
  assertIncluded(
    definition.name,
    summary,
    availableTags,
    policy.requiredAiEntitlements,
    'aiEntitlements',
  )
  assertIncluded(
    definition.name,
    summary,
    availableTags,
    policy.requiredAutomationEntitlements,
    'automationEntitlements',
  )

  if (policy.quotaMetric) {
    const remaining = getQuotaRemaining(summary, policy.quotaMetric)
    if (remaining !== null && remaining <= 0) {
      throw new AssistantToolError(
        ASSISTANT_TOOL_ERROR_CODES.quotaExceeded,
        `JIAN quota exceeded for ${definition.name}.`,
        {
          details: {
            toolName: definition.name,
            quotaMetric: policy.quotaMetric,
            remaining,
          },
        },
      )
    }
  }
}
